import { humanReadableField } from '~/filters/humanReadableField';
import { newTrigramTypePipe } from '~/filters/newTrigramTypePipe';
import { NodeEliot } from '~/interfaces/eliot.interface';
import { FieldObject, mapSubfields } from '~/interfaces/field.interface';
import { treeNodeKey, TreeNodeObject } from '~/interfaces/helper.interface';
import { EventFrequencies } from '~/interfaces/homeSecurity.interface';
import { MappedOwner, OwnerType } from '~/interfaces/owners.interface';
import { humanReadableDate } from '~common-filters/humanReadableDate';
import { PopupModule } from '~common-store/modules/popup';

export function uuidv4(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        // tslint:disable:no-bitwise
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

export function filterOnSearchText(item: TreeNodeObject | NodeEliot, search: string, keysToSearch: treeNodeKey[]): boolean {
    // case insensitive regex of the searched string
    const searchRegExp = new RegExp(search, 'i');
    // keys in which we want to find the searched string
    // if the searched string is found in at least one of the searched keys values
    // (raw or piped values depending on the key we are trying to match), return true
    return keysToSearch.some((key) => {
        const valueFoundInRawValue = item[key] && (item[key] as string).search(searchRegExp) > -1;
        const valueFoundInPipedValue =
            key === 'subType'
            && item[key]
            && (newTrigramTypePipe(item[key] as string) as string).search(searchRegExp) > -1;
        return valueFoundInRawValue || valueFoundInPipedValue;
    });
}

export function mapUsersDetails(users: OwnerType[]): MappedOwner[] {
    // iterate through users details and create correct link depending on the
    // owner (netatmo, legrand, sbdg, vaillant...)
    const adminEnv = process.env.VUE_APP_PREFIX;
    const legrandUrl = process.env.VUE_APP_ADMIN_LEGRAND_URL;
    return users.map((user) => {
        try {
            // if mail contains a '+', eliot api returns an error so we search by id instead
            const userIdentifier = user.owner === 'legrand' && user.mail.includes('+') ? user.id : user.mail;
            const externalLinkPrefix = adminEnv === 'netatmo' && user.owner === 'legrand' ? legrandUrl + '/support' : '';
            return {
                ...user,
                // link associated to displayed mail
                link: `${user.owner === 'legrand' ? 'eliotuser' : 'user'}/${userIdentifier}`,
                // prefix to determine if we have to change adminsupport
                externalLinkPrefix,
            };
        } catch (error) {
            console.error(error);
            return {};
        }
    }) as MappedOwner[];
}

export function uppercaseFirstCharacter(string: string): string {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function valueIncludesFilterValue(value: string | number | undefined): boolean {
    return value
        ? value.toString().toUpperCase().includes(PopupModule.filterValue.toUpperCase())
        : false;
}

export function formatDate(date: string): string {
    const parsedDate = Date.parse(date);
    if (isNaN(parsedDate)) {
        return 'N/A';
    }
    const d = new Date(parsedDate);
    return d.toUTCString();
}

export function displayData(field: FieldObject) {
    if (field.value == null) {
        return 'N/A';
    }

    if (field.key === 'macAddress') {
        return safeTags(field.value);
    }

    switch (field.type) {
        case 'boolean':
            return safeTags(field.value) === 'true' ? 'True' : 'False';
        case 'string':
            return safeTags(uppercaseFirstCharacter(field.value));
        case 'subField':
            return field.subfield
            && humanReadableField(field.subfield)
            && mapSubfields[field.subfield][field.value]
                ? `${mapSubfields[field.subfield][field.value]} (${field.value})`
                : safeTags(field.value);

        case 'last_disconnection':
        case 'date': {
            const seconds = field.value.sec ? field.value.sec : field.value;
            return safeTags(humanReadableDate(seconds, 'full'));
        }
        case 'formattedDate':
            return formatDate(field.value);
        case 'duration': {

            const day = Math.floor(field.value / (24 * 3600));
            const hours = Math.floor((field.value % (24 * 3600)) / 3600);
            const mins = Math.floor((field.value % 3600) / 60);

            return safeTags((day ? day + 'd ' : '') +
                ((hours < 10) ? '0' : '') + hours + ':' +
                ((mins < 10) ? '0' : '') + mins);
        }
        case 'array': {
            let res = '';

            for (const currField of field.value) {
                res += safeTags(currField) + '<br />';
            }
            return res;
        }
        case 'percent':
            return Math.round(field.value * 100) / 100 + ' %';
        case 'temperature':
            return `${field.value}°C`;
        case 'alert_record': {

            let name = '';
            switch (field.value) {
                case 'empty':
                    name = 'When nobody\'s at home';
                    break;
                case 'record_and_notify':
                    name = 'Record and notify';
                    break;
                default:
                    name = field.value + '';
                    break;
            }
            return safeTags(name.charAt(0).toUpperCase() + name.slice(1));
        }
        case 'frequency':
            switch (field.value) {
                case EventFrequencies.TWO_DAYS:
                    return 'Two days';
                case EventFrequencies.ONE_WEEK:
                    return 'One week';
                case EventFrequencies.ONE_MONTH:
                    return 'One month';
                case EventFrequencies.THREE_MONTHS:
                    return 'Three months';
                case EventFrequencies.ONE_YEAR:
                    return 'One year';
                case EventFrequencies.NEVER:
                    return 'Never';
                default:
                    return field.value;
            }
        case 'number':
            return field.value < 0 ? 'N/A' : field.value;
        default:
            return safeTags(field.value);
    }
}

export function safeTags(str?: string): string {
    str = str + '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function macToBase10(mac: string): number {
    return parseInt('0x' + mac.replace(/:/g, ''), 16);
}

export const encodeUriDeviceId = (deviceId: string) => {
    const patterns = ['://', '#'];

    if (!deviceId) {
        return;
    }

    return patterns.reduce((acc, pattern) => {
        return acc.replaceAll(pattern, encodeURIComponent(pattern));
    }, deviceId);
};